<template>
  <div v-if="visible" :class="inline ? 'inline-wrap' : 'mask'" @click.self="onMaskClick">
    <div class="dialog dialog--shell dialog--create" @click.stop>
      <div class="dialog-head">
        <h3 class="dialog-title">编辑报名信息</h3>
        <button type="button" class="dialog-close" aria-label="关闭" @click="onCancel">×</button>
      </div>

      <div class="dialog-body">
        <p class="dlg-meta">提交 #{{ submissionId }} · 布局与小程序报名表一致，修改后保存即更新该条报名</p>
        <p v-if="errorMsg" class="dialog-error">{{ errorMsg }}</p>
        <p v-if="loading" class="muted">加载中…</p>

        <template v-else>
          <div class="dialog-section">
            <div class="dialog-section-head">
              <h4 class="dialog-section-title">报名信息收集</h4>
              <button type="button" class="btn btn-edit btn-sm" @click="addAttendee">+ 添加参会人</button>
            </div>

            <div v-if="attendees.length" class="dialog-pill-row">
              <button
                v-for="(person, idx) in attendees"
                :key="'pill-' + idx"
                type="button"
                class="dialog-pill"
                :class="{ 'dialog-pill--on': idx === selectedIdx }"
                @click="selectedIdx = idx"
              >
                <span>{{ pillLabel(person, idx) }}</span>
                <span
                  v-if="attendees.length > 1"
                  class="dialog-pill-x"
                  title="移除"
                  @click.stop="removeAttendee(idx)"
                >×</span>
              </button>
            </div>

            <p v-if="!attendees.length" class="muted hint-line">暂无参会人，请点击「添加参会人」。</p>

            <div v-if="currentPerson" class="attendee-block">
              <h4 class="dialog-section-title">基本信息</h4>
              <div class="dialog-field">
                <input
                  v-model="currentPerson.name"
                  class="flt-input"
                  placeholder="请输入姓名（必填）"
                />
                <span v-if="fieldErr('name')" class="field-err">{{ fieldErr('name') }}</span>
              </div>
              <div class="dialog-field">
                <input
                  v-model="currentPerson.phone"
                  class="flt-input"
                  type="tel"
                  maxlength="11"
                  placeholder="请输入11位手机号（必填）"
                />
                <span v-if="fieldErr('phone')" class="field-err">{{ fieldErr('phone') }}</span>
              </div>
              <div class="dialog-field">
                <input
                  v-model="currentPerson.company"
                  class="flt-input"
                  placeholder="请输入单位名称（必填）"
                />
                <span v-if="fieldErr('company')" class="field-err">{{ fieldErr('company') }}</span>
              </div>
              <div class="dialog-field">
                <input
                  v-model="currentPerson.title"
                  class="flt-input"
                  placeholder="请输入单位职务（必填）"
                />
                <span v-if="fieldErr('title')" class="field-err">{{ fieldErr('title') }}</span>
              </div>

              <template v-if="collectFields.length">
                <h4 class="dialog-section-title dialog-section-title--gap">活动信息收集</h4>
                <div v-for="f in collectFields" :key="f.fieldKey" class="dialog-field">
                  <template v-if="normFieldType(f) === 'TEXT' || normFieldType(f) === 'NUMBER'">
                    <input
                      v-model="currentPerson[f.fieldKey]"
                      class="flt-input"
                      :type="normFieldType(f) === 'NUMBER' ? 'number' : 'text'"
                      :placeholder="fieldPlaceholder(f)"
                    />
                  </template>
                  <textarea
                    v-else-if="normFieldType(f) === 'TEXTAREA'"
                    v-model="currentPerson[f.fieldKey]"
                    class="flt-textarea"
                    rows="3"
                    :placeholder="fieldPlaceholder(f)"
                  />
                  <select
                    v-else-if="normFieldType(f) === 'SELECT'"
                    v-model="currentPerson[f.fieldKey]"
                    class="flt-input"
                  >
                    <option value="">{{ selectPlaceholder(f) }}</option>
                    <option v-for="opt in fieldOptions(f)" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </select>
                  <template v-else-if="normFieldType(f) === 'RADIO'">
                    <span class="dialog-field-label">
                      <em v-if="f.required">*</em>{{ f.label || f.fieldKey }}
                    </span>
                    <div class="dialog-opt-list">
                      <button
                        v-for="opt in fieldOptions(f)"
                        :key="`${f.fieldKey}-${opt.value}`"
                        type="button"
                        class="dialog-opt-chip"
                        :class="{ 'dialog-opt-chip--on': currentPerson[f.fieldKey] === opt.value }"
                        @click="setField(f.fieldKey, opt.value)"
                      >
                        {{ opt.label }}
                      </button>
                    </div>
                  </template>
                  <template v-else-if="isCheckGroup(f)">
                    <span class="dialog-field-label">
                      <em v-if="f.required">*</em>{{ f.label || f.fieldKey }}
                    </span>
                    <div class="dialog-opt-list">
                      <button
                        v-for="opt in fieldOptions(f)"
                        :key="`${f.fieldKey}-${opt.value}`"
                        type="button"
                        class="dialog-opt-chip"
                        :class="{ 'dialog-opt-chip--on': checked(f.fieldKey, opt.value) }"
                        @click="toggleCheck(f.fieldKey, opt.value)"
                      >
                        {{ opt.label }}
                      </button>
                    </div>
                  </template>
                  <input v-else v-model="currentPerson[f.fieldKey]" class="flt-input" :placeholder="fieldPlaceholder(f)" />
                  <span v-if="fieldErr(f.fieldKey)" class="field-err">{{ fieldErr(f.fieldKey) }}</span>
                </div>
              </template>
            </div>
          </div>
        </template>
      </div>

      <div v-if="!loading" class="dialog-foot">
        <button type="button" class="btn btn-cancel" :disabled="saving" @click="onCancel">取消</button>
        <button type="button" class="btn btn-primary" :disabled="saving || !canAct" @click="save">
          {{ saving ? '保存中…' : '保存' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getAdminCmsActivity,
  getAdminActivitySignupRawPayload,
  updateAdminActivitySignupPayload,
} from '@/api'
import {
  parseActivityCollectFields,
  payloadToFormAttendees,
  formAttendeesToPayload,
  newAttendee,
  pillLabel,
  validateAttendees,
  normFieldType,
  isCheckGroup,
  fieldOptions,
} from '@/utils/activitySignupForm'

export default {
  name: 'ActivitySignupEditDialog',
  props: {
    visible: { type: Boolean, default: false },
    activityId: { type: [Number, String], default: 0 },
    submissionId: { type: [Number, String], default: 0 },
    /** 从名单行打开时，预选对应参会人槽位 */
    initialSlotIndex: { type: Number, default: 0 },
    canAct: { type: Boolean, default: true },
    /** 独立页面模式（路由 /signup-edit/...），非遮罩弹层 */
    inline: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      saving: false,
      errorMsg: '',
      collectFields: [],
      basePayload: {},
      attendees: [],
      selectedIdx: 0,
      fieldErrors: {},
    }
  },
  computed: {
    currentPerson() {
      const list = this.attendees || []
      if (!list.length) return null
      const i = Math.min(Math.max(0, this.selectedIdx), list.length - 1)
      return list[i]
    },
  },
  watch: {
    visible(v) {
      if (v) this.boot()
    },
    submissionId() {
      if (this.visible) this.boot()
    },
  },
  methods: {
    pillLabel,
    normFieldType,
    isCheckGroup,
    fieldOptions,
    fieldPlaceholder(f) {
      const label = String(f?.label || f?.fieldKey || '').trim()
      const req = !!f?.required
      if (!label) return req ? '请输入（必填）' : '请输入'
      return req ? `请输入${label}（必填）` : `请输入${label}`
    },
    selectPlaceholder(f) {
      const label = String(f?.label || '').trim()
      return label ? `请选择${label}` : '请选择'
    },
    fieldErr(suffix) {
      const k = `${this.selectedIdx}-${suffix}`
      return this.fieldErrors[k] || ''
    },
    setField(key, value) {
      const p = this.currentPerson
      if (!p || !key) return
      p[key] = value
    },
    checked(key, value) {
      const p = this.currentPerson
      const arr = p?.[key]
      return Array.isArray(arr) && arr.includes(value)
    },
    toggleCheck(key, value) {
      const p = this.currentPerson
      if (!p) return
      const old = Array.isArray(p[key]) ? [...p[key]] : []
      const ix = old.indexOf(value)
      if (ix >= 0) old.splice(ix, 1)
      else old.push(value)
      p[key] = old
    },
    addAttendee() {
      this.attendees.push(newAttendee(this.collectFields))
      this.selectedIdx = this.attendees.length - 1
      this.fieldErrors = {}
    },
    removeAttendee(idx) {
      if (this.attendees.length <= 1) {
        window.alert('至少保留一位参会人')
        return
      }
      this.attendees.splice(idx, 1)
      if (this.selectedIdx >= this.attendees.length) {
        this.selectedIdx = Math.max(0, this.attendees.length - 1)
      }
      this.fieldErrors = {}
    },
    onMaskClick() {
      if (!this.inline) this.onCancel()
    },
    onCancel() {
      this.$emit('close')
    },
    async boot() {
      const aid = Number(this.activityId)
      const sid = Number(this.submissionId)
      if (!Number.isFinite(aid) || aid <= 0 || !Number.isFinite(sid) || sid <= 0) {
        this.errorMsg = '缺少活动或提交 ID'
        return
      }
      this.loading = true
      this.errorMsg = ''
      this.fieldErrors = {}
      try {
        const [act, payload] = await Promise.all([
          getAdminCmsActivity(aid),
          getAdminActivitySignupRawPayload(aid, sid),
        ])
        this.collectFields = parseActivityCollectFields(act)
        this.basePayload = payload && typeof payload === 'object' ? { ...payload } : {}
        this.attendees = payloadToFormAttendees(this.basePayload, this.collectFields)
        if (!this.attendees.length) {
          this.attendees = [newAttendee(this.collectFields)]
        }
        let idx = Number(this.initialSlotIndex)
        if (!Number.isFinite(idx) || idx < 0) idx = 0
        if (idx >= this.attendees.length) idx = 0
        this.selectedIdx = idx
      } catch (e) {
        this.errorMsg = e.message || '加载失败'
        this.attendees = []
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (!this.canAct) return
      const errs = validateAttendees(this.attendees, this.collectFields)
      if (Object.keys(errs).length) {
        this.fieldErrors = errs
        const firstKey = Object.keys(errs)[0]
        const m = errs[firstKey]
        const slot = parseInt(String(firstKey).split('-')[0], 10)
        if (Number.isFinite(slot)) this.selectedIdx = slot
        window.alert(m || '请完善必填项')
        return
      }
      this.fieldErrors = {}
      const aid = Number(this.activityId)
      const sid = Number(this.submissionId)
      const next = formAttendeesToPayload(this.attendees, this.collectFields, this.basePayload)
      this.saving = true
      this.errorMsg = ''
      try {
        await updateAdminActivitySignupPayload(aid, sid, next)
        this.$emit('saved')
        this.$emit('close')
      } catch (e) {
        this.errorMsg = e.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.inline-wrap {
  padding: 0;
}

.btn-sm {
  min-height: 32px;
  padding: 6px 14px;
  font-size: 12px;
}

.hint-line {
  margin: 0 0 12px;
}

.field-err {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #d92d20;
}
</style>
